import type ProductWizardController from "../product_wizard_controller";
import { getCurrentPriceUom, esc } from "./helpers";
import http from "lib/http";

/** Handle conversion calculation triggered by a quantity input event. */
export function calculateConversion(
  controller: ProductWizardController,
  event: Event,
): void {
  const input = event.target as HTMLInputElement;
  const index = parseInt(input.dataset.variantIndex || "0", 10);
  const quantity = parseInt(input.value, 10) || 1;
  // Determine which panel the input belongs to by checking if the
  // review content target contains this input
  const isReview =
    controller.hasReviewContentTarget &&
    controller.reviewContentTarget.contains(input);
  fetchConversion(controller, index, quantity, isReview ? "review" : "preview");
}

/** Fetch a UoM conversion result from the server. */
export async function fetchConversion(
  controller: ProductWizardController,
  variantIndex: number,
  quantity: number,
  prefix: string = "preview",
): Promise<void> {
  if (!controller.conversionUrlValue) return;

  const variants = controller.variantsContainerTarget.querySelectorAll(
    "[data-variant-entry]",
  );
  const variant = variants[variantIndex];
  if (!variant) return;

  const priceUom = getCurrentPriceUom(controller);
  if (!priceUom) return;

  const getVal = (name: string): string => {
    const el = variant.querySelector(`[name*="[${name}]"]`) as
      | HTMLInputElement
      | HTMLSelectElement
      | null;
    return el?.value || "";
  };

  const sellUom = getVal("sell_uom");
  if (!sellUom) return;

  const params = new URLSearchParams({
    price_uom: priceUom,
    sell_uom: sellUom,
    quantity: String(quantity),
  });

  // Add bridge fields if present
  const sellWeight = getVal("sell_weight");
  const sellWeightUom = getVal("sell_weight_uom");
  const sellCount = getVal("sell_count");
  const sellCountUom = getVal("sell_count_uom");

  if (sellWeight) params.set("sell_weight", sellWeight);
  if (sellWeightUom) params.set("sell_weight_uom", sellWeightUom);
  if (sellCount) params.set("sell_count", sellCount);
  if (sellCountUom) params.set("sell_count_uom", sellCountUom);

  const getChecked = (name: string): boolean => {
    const el = variant.querySelector(
      `[name*="[${name}]"]`,
    ) as HTMLInputElement | null;
    return el?.type === "checkbox" ? el.checked : el?.value === "true";
  };
  params.set(
    "sell_approximate",
    getChecked("sell_approximate") ? "true" : "false",
  );
  params.set(
    "sell_weight_approximate",
    getChecked("sell_weight_approximate") ? "true" : "false",
  );

  const resultEl = document.getElementById(
    `${prefix}-variant-${variantIndex}-result`,
  );
  if (!resultEl) return;

  try {
    const response = await http.get<{
      description?: string;
      converted?: string;
      price_uom_name?: string;
      approximate?: boolean;
      error?: string;
    }>(`${controller.conversionUrlValue}?${params.toString()}`);
    const data = response.data;
    if (data.description) {
      resultEl.textContent = `= ${data.converted} ${data.price_uom_name}${data.approximate ? " (approx.)" : ""}`;
      resultEl.classList.remove("text-red-500");
      resultEl.classList.add("text-gray-700", "font-medium");
    } else if (data.error) {
      resultEl.innerHTML = `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 text-xs">${esc(data.error)}</span>`;
      resultEl.classList.remove("text-red-500", "text-gray-700", "font-medium");
    }
  } catch {
    resultEl.textContent = "\u2014";
  }
}
